/*****************************************************************************************
 * 설명 : 관리자 > 컨텐츠관리 > 모듈 관리
 * URI : /admin/site
*****************************************************************************************/
import Dialog from '@mui/material/Dialog';
import { AgGridReact } from 'ag-grid-react';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from "yup";

import BtnAgGridSave from 'components/btnAgGridSave';
import ChipEx from 'components/chipEx';
import { BtnRefresh, BtnSearch } from 'components/common';
import { ButtonEx, SelectEx } from 'components/inputEx';

import { Restful } from 'service/restful';
import { comma, getSelectData, PaperComponent } from 'service/utils';

import { MESSAGE_DELAY } from 'config/config';

import useGridConfig from 'hooks/useGridConfig';

import alertMsg from 'components/message';
import ModuleModal from 'pages/admin/cms/moduleModal';
import Layout from 'pages/admin/layout/layout';

/*****************************************************************************************
 * 설명 : 함수 선언
*****************************************************************************************/
const Module = ( props ) => {

  /***************************************************************************************
   * 설명 : 변수 선언부
  ***************************************************************************************/
  const [gridApi, setGridApi] = useState({});
  const [getApi] = Restful();

  const currentMenu = useSelector(state => state?.menuList?.current);

  const [list, setList] = useState([]);
  const [siteList, setSiteList] = useState([]);

  const [openModal, setOpenModal] = useState({open: false, modal: 'add', data: []});

  const formik = useFormik({
    initialValues: {
      siteSeq: null,
    },
    validationSchema: Yup.object().shape({
    }),
    onSubmit: (values) => {
        getModuleInfo();
    }
  });

  // table column
  const [columnDefs, setColumnDefs] = useState([
    { headerName: '관리번호(관리자용)', field: 'seq', width: 30, cellClass: 'center', flex: 0,
      headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true },
    { headerName: '정렬', field: 'sortOrder', width: 52, cellClass: 'cp center', rowDrag: true, filter: false, editable: false, resize: false },
    { headerName: '번호', field: 'rowIndex', width: 55, cellClass: 'cp right',
      valueGetter: (params) => {
        return comma(params.node.rowIndex + 1);
      }
    },
    { headerName:'사이트', field: 'siteName', width: 250, cellClass: 'cp' },
    { headerName:'모듈', field: 'module', width: 200, cellClass: 'cp' },
    { headerName:'모듈명', field: 'moduleName', width: 300, cellClass: 'cp' },
    { headerName:'모듈패스', field: 'modulePath', width: 400, cellClass: 'cp' },
    { headerName: '사용여부', field: 'isUse', width: 100, cellClass: 'cp center',
      cellRenderer: function(params) {
        if( params.data.isUse === '1' || params.data.isUse === 1 )
          return <ChipEx color="success" variant="outlined" size="small" label="사용" />;
        else
          return <ChipEx color="default" variant="outlined" size="small" label="사용안함" />;
      }
    },
    {headerName: '수정', field: 'modify', cellClass: 'cp center', width: 100,
      cellRenderer: function (params) {
        return <ButtonEx title="수정" auth="authModify" color="secondary" variant="outlined" className="gridBtn" onClick={() => setOpenModal({open: true, modal: 'add', data: params.data})} />;
      }
    }
  ]);

  const [gridConfig, setGridUpdate] = useGridConfig(1, setColumnDefs);

  /***************************************************************************************
   * 설명 : 모듈 가져오기
  ***************************************************************************************/
  const getModuleInfo = () => {
    let params = {
      program: 'api',
      service: 'cms',
      action: 'getModuleInfo',
      version: '1.0',
      siteSeq: formik.values.siteSeq
    }

    getApi("get", params).then( response => {
      if( response !== undefined && response.data.result ) {
        setList(response.data.data);
      } else {
        setList([]);
      }
    })
  }

  /***************************************************************************************
   * 설명 : 사이트 정보 가져오기
  ***************************************************************************************/
  const getSiteInfo = () => {
    let params = {
      program: 'api',
      service: 'cms',
      action: 'getSiteInfo',
      version: '1.0',
      domain: '',
      uri: ''
    }

    setSiteList([]);

    getApi("get", params).then( response => {
      if( response !== undefined && response.data.result ) {
        setSiteList(getSelectData(response.data.siteInfo, 'siteName', 'seq'));
      } else {
        setSiteList([]);
      }
    })
  }

  /***************************************************************************************
   * 설명 : 모듈 삭제
  ***************************************************************************************/
  const deleteModule = () => {
    let nodes = gridApi.getSelectedRows();

    if( nodes.length < 1 ) {
      alertMsg("삭제할 모듈을 선택하시기 바랍니다.", "W", MESSAGE_DELAY);
      return;
    }

    if( ! window.confirm("선택한 모듈을 삭제하시겠습니까?") ) return;

    let params = {
      program: 'api',
      service: 'cms',
      action: 'deleteModuleInfo',
      version: '1.0',
    }

    params.seq = nodes.map((item) => item.seq).join();

    getApi("post", params).then( response => {
      if( response !== undefined && response.data.result ) {
        getModuleInfo();
      } else {
        alertMsg(response?.data?.message || '서버와의 통신에 실패하였습니다.', 'E', MESSAGE_DELAY);
      }
    });
  }

  /***************************************************************************************
   * 설명 : 순서 저장
  ***************************************************************************************/
  const setSortModule = () => {
    let nodes = gridApi.getSelectedRows();

    if( nodes.length < 1 ) {
      if( ! window.confirm("모듈 전체의 순서를 저장하시겠습니까?") ) return;

      nodes = [];
      gridApi.forEachNode((item) => {
        nodes.push(item.data);
      });
    }

    if( ! window.confirm("선택한 모듈의 순서를 저장하시겠습니까?") ) return;

    let params = {
      program: 'api',
      service: 'cms',
      action: 'setSortModule',
      version: '1.0',
    }

    params.seq = nodes.map((item) => item.seq).join();

    getApi("post", params).then( response => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        getModuleInfo();
      } else {
        alertMsg(response?.data?.message || '서버와의 통신에 실패하였습니다.', 'E', MESSAGE_DELAY);
      }
    });
  }

  /***************************************************************************************
   * 설명 : 사이트 변경 시 검색
  ***************************************************************************************/
  useEffect(() => {
    if( formik.values.siteSeq !== '' && formik.values.siteSeq !== null )
      getModuleInfo();

    // eslint-disable-next-line
  }, [formik.values.siteSeq]);

  /***************************************************************************************
   * 설명 : 데이터 처리
  ***************************************************************************************/
  useEffect(() => {
    getSiteInfo();

    // eslint-disable-next-line
  }, [window.location.search]);

  /***************************************************************************************
   * 설명 : html 선언부
  ***************************************************************************************/
  return (
    <Layout>
      <div className="admin-cms-contents">
        <form onSubmit={formik.handleSubmit}>
          <header className="admin-cms-search-header">
            <div className="left">
              <BtnRefresh click={() => {
                formik.setFieldValue('siteSeq', null);
                formik.handleSubmit();
              }}></BtnRefresh>
              <BtnSearch click={() => getModuleInfo()}></BtnSearch>
              <BtnAgGridSave click={setGridUpdate}></BtnAgGridSave>

              <SelectEx
                name="siteSeq"
                formik={formik}
                fullWidth={false}
                data={[
                  {label: '공통', value: ''}
                ].concat(siteList)}
              />
            </div>

            <div className="right">
              <ButtonEx
                title="순서저장"
                auth="authModify"
                color="inherit"
                variant="outlined"
                className="Btn"
                onClick={() => setSortModule()}
              />

              <ButtonEx
                title="모듈삭제"
                auth="authDelete"
                color="error"
                variant="outlined"
                className="ml5"
                onClick={() => deleteModule()}
              />

              <ButtonEx
                title="모듈등록"
                auth="authWrite"
                color="primary"
                className="ml5"
                variant="contained"
                onClick={() => {
                  setOpenModal({open: true, modal: 'add', data: {siteSeq: formik.values.siteSeq}})
                }}
              />
            </div>
          </header>
        </form>

        <section className="admin-cms-body">
          <section className="ag-theme-balham grid" style={{height:'calc(100% - 50px)'}}>
            <AgGridReact
              defaultColDef={{
                sortable: true,
                resizable: true,
                filter: false,
                lockVisible: true
              }}
              rowSelection = {'multiple'}
              columnDefs={columnDefs}
              rowData={list}
              onGridReady={(event) => {
                gridConfig(event.columnApi);
                setGridApi(event.api);
              }}
              onRowDoubleClicked={(event) => {
                if( currentMenu?.authModify === '1' || currentMenu?.authModify === 1 )
                  setOpenModal({open: true, modal: 'add', data: event.data});
              }}
              rowDragManaged={true}
              rowDragMultiRow={true}
              animateRows={true}
              overlayNoRowsTemplate = "검색된 내용이 없습니다."
            />
          </section>
        </section>
      </div>

      { openModal.open &&
        <Dialog
          open={openModal.open}
          onClose={() => (event, reason) => {
            if(reason && reason === "backdropClick") return;
            setOpenModal({open: false, modal: openModal.modal, data: openModal.data});
          }}
          PaperComponent={PaperComponent}
          aria-labelledby="draggable-dialog-title"
          sx={{
            "& .MuiDialog-container": {
              "& .MuiPaper-root": {
                width: "100%",
                maxWidth: "750px",  // Set your width here
              },
            },
          }}
        >
          <ModuleModal
            open={openModal.open}
            close={() => setOpenModal({ open: false, modalName: 'add', data: openModal.data })}
            data={openModal.data}
            getList={getModuleInfo}
            indicator={props.indicator}
            history={props.history}
          />
        </Dialog>
      }
    </Layout>
  );
}

/*****************************************************************************************
 * 설명 : default export 선언
*****************************************************************************************/
export default Module;